import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X } from 'lucide-react';
import { useQuery } from 'urql';
import { Q_CLIENTES } from '@/modules/administracion/clientes/clientes.gql.js';
import './ClienteCombobox.css';

const DROPDOWN_GAP = 6;
const DROPDOWN_MAX_HEIGHT = 280;
const DROPDOWN_MIN_HEIGHT = 216;
const VIEWPORT_PADDING = 12;

function getClienteCode(cliente) {
  return cliente?.codigo || cliente?.codigoCliente || cliente?.codigo_cliente || cliente?.id || null;
}

function getClienteLabel(cliente) {
  const nombre = cliente?.razonSocial || cliente?.nombreFantasia || cliente?.nombre || '';
  const codigo = getClienteCode(cliente);
  return codigo ? `${codigo} · ${nombre}` : nombre;
}

/**
 * ClienteCombobox - Select con búsqueda en tiempo real via GraphQL
 *
 * Features:
 * - Búsqueda incremental mientras escribís
 * - Dropdown con resultados filtrados
 * - Selección con click o teclado
 * - Auto-completado de datos del cliente
 * - Loading states
 */
export default function ClienteCombobox({
  id,
  name,
  label,
  value,
  selectedCliente = null,
  onChange,
  onClienteSelect,
  error,
  placeholder = 'Buscar cliente...',
  disabled = false,
  excludeId = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [dropdownPlacement, setDropdownPlacement] = useState('bottom');
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputContainerRef = useRef(null);
  const dropdownRef = useRef(null);

  const [{ data, fetching }] = useQuery({
    query: Q_CLIENTES,
    variables: {
      page: 1,
      pageSize: 50,
      filter: search.length >= 2 ? { search, activo: true } : { activo: true },
    },
    pause: !isOpen && search.length < 2,
  });

  const clientes = data?.clientes?.edges?.map(edge => edge.node) ?? [];
  const normalizedValue = value === null || value === undefined ? '' : String(value);
  const normalizedExcludeId = excludeId === null || excludeId === undefined ? '' : String(excludeId);
  const filteredClientes = excludeId
    ? clientes.filter(c => String(c.id) !== normalizedExcludeId)
    : clientes;
  const filteredClienteIds = filteredClientes.map(cliente => String(cliente.id)).join('|');

  const selectedClienteFromQuery = filteredClientes.find(c => String(c.id) === normalizedValue)
    || clientes.find(c => String(c.id) === normalizedValue);
  const resolvedSelectedCliente = selectedClienteFromQuery || selectedCliente;
  const displayValue = getClienteLabel(resolvedSelectedCliente);
  const shouldRenderDropdown = isOpen && typeof document !== 'undefined';

  const recalculateDropdownPosition = useCallback(() => {
    if (typeof window === 'undefined' || !inputContainerRef.current) return;

    const rect = inputContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = Math.max(viewportHeight - rect.bottom - DROPDOWN_GAP - VIEWPORT_PADDING, 120);
    const spaceAbove = Math.max(rect.top - DROPDOWN_GAP - VIEWPORT_PADDING, 120);
    const openUpward = spaceBelow < DROPDOWN_MIN_HEIGHT && spaceAbove > spaceBelow;
    const availableHeight = openUpward ? spaceAbove : spaceBelow;
    const maxWidth = viewportWidth - (VIEWPORT_PADDING * 2);
    const width = Math.min(Math.max(rect.width, 240), maxWidth);
    const left = Math.min(Math.max(rect.left, VIEWPORT_PADDING), viewportWidth - width - VIEWPORT_PADDING);

    setDropdownPlacement(openUpward ? 'top' : 'bottom');
    setDropdownStyle({
      left,
      top: openUpward ? undefined : rect.bottom + DROPDOWN_GAP,
      bottom: openUpward ? Math.max(viewportHeight - rect.top + DROPDOWN_GAP, VIEWPORT_PADDING) : undefined,
      width,
      maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, availableHeight),
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !wrapperRef.current?.contains(event.target) &&
        !dropdownRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!shouldRenderDropdown) return undefined;

    recalculateDropdownPosition();

    const handleViewportChange = () => {
      recalculateDropdownPosition();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [shouldRenderDropdown, recalculateDropdownPosition, filteredClientes.length, fetching, search]);

  useEffect(() => {
    if (!isOpen || filteredClientes.length === 0) {
      setSelectedIndex(-1);
      return;
    }

    setSelectedIndex(previousIndex => (
      previousIndex >= 0 && previousIndex < filteredClientes.length ? previousIndex : 0
    ));
  }, [filteredClienteIds, filteredClientes.length, isOpen]);

  const handleInputChange = (e) => {
    setSearch(e.target.value);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleSelectCliente = (cliente) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: String(cliente.id),
        },
      });
    }

    if (onClienteSelect) {
      onClienteSelect(cliente);
    }

    setSearch('');
    setIsOpen(false);
    setSelectedIndex(-1);
    setDropdownStyle(null);
  };

  const handleSelectClienteFromPointer = (event, cliente) => {
    event.preventDefault();
    event.stopPropagation();
    handleSelectCliente(cliente);
  };

  const handleClear = () => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
        },
      });
    }

    if (onClienteSelect) {
      onClienteSelect(null);
    }

    setSearch('');
    setIsOpen(false);
    setDropdownStyle(null);
  };

  const handleOpenEditor = () => {
    if (disabled) return;
    setSearch('');
    setIsOpen(true);
    setSelectedIndex(-1);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      recalculateDropdownPosition();
    });
  };

  const handleKeyDown = (e) => {
    if (!isOpen || fetching || filteredClientes.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        requestAnimationFrame(() => recalculateDropdownPosition());
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredClientes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        {
          const clienteIndex = selectedIndex >= 0 && selectedIndex < filteredClientes.length
            ? selectedIndex
            : 0;
          handleSelectCliente(filteredClientes[clienteIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearch('');
        setDropdownStyle(null);
        break;
      default:
        break;
    }
  };

  const dropdownContent = shouldRenderDropdown && dropdownStyle
    ? createPortal(
      <div
        ref={dropdownRef}
        className={`cliente-combobox__dropdown cliente-combobox__dropdown--${dropdownPlacement}`}
        style={dropdownStyle}
      >
        {fetching ? (
          <div className="cliente-combobox__loading">
            <div className="cliente-combobox__spinner" />
            <span>Buscando clientes...</span>
          </div>
        ) : filteredClientes.length === 0 ? (
          <div className="cliente-combobox__empty">
            {search.length >= 2
              ? 'No se encontraron clientes con ese criterio'
              : 'Escribí al menos 2 caracteres para buscar'}
          </div>
        ) : (
          <ul className="cliente-combobox__list">
            {filteredClientes.map((cliente, index) => (
              <li
                key={cliente.id}
                className={`cliente-combobox__item ${
                  index === selectedIndex ? 'cliente-combobox__item--selected' : ''
                }`}
                onMouseDown={(event) => handleSelectClienteFromPointer(event, cliente)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="cliente-combobox__item-main">
                  <span className="cliente-combobox__item-title">
                    {getClienteLabel(cliente)}
                  </span>
                  {cliente.cuit && (
                    <span className="cliente-combobox__item-subtitle">
                      CUIT: {cliente.cuit}
                    </span>
                  )}
                </div>
                {cliente.direccion && (
                  <span className="cliente-combobox__item-hint">{cliente.direccion}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="cliente-combobox">
      {label && (
        <label htmlFor={id} className="cliente-combobox__label">
          {label}
        </label>
      )}

      <div ref={wrapperRef} className="cliente-combobox__wrapper">
        <div
          ref={inputContainerRef}
          className={`cliente-combobox__input-container ${error ? 'cliente-combobox__input-container--error' : ''}`}
        >
          {value && !isOpen ? (
            <div
              className={`cliente-combobox__selected ${disabled ? '' : 'cliente-combobox__selected--interactive'}`}
              onClick={handleOpenEditor}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenEditor();
                }
              }}
              role={disabled ? undefined : 'button'}
              tabIndex={disabled ? -1 : 0}
              aria-label={displayValue ? `Cambiar cliente ${displayValue}` : 'Cambiar cliente'}
            >
              <span className="cliente-combobox__selected-text">{displayValue}</span>
              {!disabled && (
                <button
                  type="button"
                  className="cliente-combobox__clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  aria-label="Limpiar selección"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ) : (
            <div className="cliente-combobox__search-wrapper">
              <Search size={16} className="cliente-combobox__icon" />
              <input
                ref={inputRef}
                id={id}
                type="text"
                className="cliente-combobox__input"
                value={search}
                onChange={handleInputChange}
                onFocus={() => {
                  setIsOpen(true);
                  recalculateDropdownPosition();
                }}
                onKeyDown={handleKeyDown}
                placeholder={value ? displayValue : placeholder}
                disabled={disabled}
                autoComplete="off"
              />
            </div>
          )}

          {!value && (
            <ChevronDown
              size={16}
              className={`cliente-combobox__chevron ${isOpen ? 'cliente-combobox__chevron--open' : ''}`}
            />
          )}
        </div>
      </div>

      {dropdownContent}
      {isOpen && !disabled ? (
        <p className="mt-1 text-xs text-[rgba(var(--muted),0.9)]">
          ↑ ↓ para recorrer · Enter o Tab para seleccionar
        </p>
      ) : null}
      {error && <span className="cliente-combobox__error">{error}</span>}
    </div>
  );
}
